import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Game } from "../../types/models";
import { useGameClock } from "../../hooks/useGameClock";
import {
  ClockUpdate,
  MAX_PERIOD_MINUTES,
  clockReadingOf,
  formatClock,
  isClockFinished,
  periodLabel,
  periodLengthSeconds,
  regulationPeriodsOf,
} from "../../utils/gameClock";
import "./Scoreboard.css";

interface ClockControlProps {
  game: Game;
  /** The league's period lengths, one per period. */
  periodMinutes?: number[];
  isSaving: boolean;
  onChange: (changes: ClockUpdate) => void;
}

/** Keep only the digits a clock field can hold. */
function digits(value: string, max = 2): string {
  return value.replace(/\D/g, "").slice(0, max);
}

/**
 * The game clock, and the way a scorekeeper runs it.
 *
 * At rest this is the time, the period, and two buttons: play/pause, and a
 * pencil that opens the form for setting the clock. The time ticks locally
 * between saves, so the scoreboard reads like a scoreboard rather than
 * updating only when something is entered.
 *
 * A period that runs out leaves the clock sitting at the end — nothing is
 * saved and the period does not change on its own, because the scorekeeper is
 * the one who knows the next period has started. Opening the form then offers
 * the next period, already filled in with its length, so starting a period is
 * a confirm rather than a re-entry.
 *
 * There is no read-only mode: GameScoringPage turns the whole scoreboard away
 * from anyone who cannot score the game.
 */
export default function ClockControl({
  game,
  periodMinutes,
  isSaving,
  onChange,
}: ClockControlProps) {
  const [isEditing, setIsEditing] = useState(false);
  const seconds = useGameClock(game);
  const reading = clockReadingOf(game);
  const regulationPeriods = regulationPeriodsOf(periodMinutes);
  // A clock with no length and no time on it has never been set, so there is
  // nothing to run yet — and no period that has run out. A new game reads 0:00,
  // which a countdown would otherwise take for a period played to its end.
  const isUnset = game.clock_limit_seconds === null && seconds === 0;
  const isFinished = !isUnset && isClockFinished(reading, seconds);
  const canRun = !isUnset && !(isFinished && !game.clock_running);

  // What the form opens on. A period that has run out is done, so the form
  // moves on to the next one; otherwise it edits the period being played.
  const editingPeriod = isFinished
    ? game.current_period + 1
    : game.current_period;
  const editingLength =
    (isFinished
      ? periodLengthSeconds(
          editingPeriod,
          periodMinutes,
          game.clock_limit_seconds,
        )
      : game.clock_limit_seconds ??
        periodLengthSeconds(editingPeriod, periodMinutes, null)) ?? 0;
  // A period being set up starts full counting down and at zero counting up,
  // whether it follows one that ran out or is the first clock this game has
  // had. Only a correction to a period already under way opens on the time
  // now showing — which for an unset clock would be a 0:00 that cannot run.
  const isFreshPeriod = isFinished || isUnset;
  const editingSeconds = isFreshPeriod
    ? reading.direction === "up"
      ? 0
      : editingLength
    : seconds;

  const toggleRunning = () => onChange({ clock_running: !game.clock_running });

  return (
    <>
      <div className="sb-clock">
        <div className="sb-clock-time" aria-label="Game clock">
          {formatClock(seconds)}
        </div>
        <div className="sb-clock-period">
          {periodLabel(game.current_period, regulationPeriods)}
          {reading.direction === "up" && (
            <span className="sb-clock-direction"> ▲</span>
          )}
        </div>
        <div className="sb-clock-buttons">
          <Button
            size="sm"
            variant={game.clock_running ? "outline-danger" : "success"}
            className="sb-clock-run"
            disabled={isSaving || !canRun}
            onClick={toggleRunning}
            aria-label={game.clock_running ? "Pause clock" : "Start clock"}
          >
            <i
              className={`bi bi-${game.clock_running ? "pause" : "play"}-fill`}
              aria-hidden="true"
            />
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="sb-clock-edit"
            disabled={isSaving}
            onClick={() => setIsEditing(true)}
            aria-label="Set clock and period"
          >
            <i className="bi bi-pencil" aria-hidden="true" />
          </Button>
        </div>
        {isUnset && <div className="sb-clock-hint">Set the clock to start</div>}
        {!isUnset && isFinished && !game.clock_running && (
          <div className="sb-clock-hint">
            {periodLabel(game.current_period, regulationPeriods)} is over
          </div>
        )}
      </div>

      {isEditing && (
        <ClockEditModal
          period={editingPeriod}
          lengthSeconds={editingLength}
          clockSeconds={editingSeconds}
          direction={reading.direction}
          regulationPeriods={regulationPeriods}
          startingNewPeriod={isFinished}
          isSaving={isSaving}
          onCancel={() => setIsEditing(false)}
          onSave={(changes) => {
            setIsEditing(false);
            // Setting the clock always leaves it stopped: the puck drops when
            // the scorekeeper presses play, not when the form closes.
            onChange({ ...changes, clock_running: false });
          }}
        />
      )}
    </>
  );
}

interface ClockEditModalProps {
  period: number;
  lengthSeconds: number;
  clockSeconds: number;
  direction: "down" | "up";
  regulationPeriods: number;
  /** True when the period being edited has not been played yet. */
  startingNewPeriod: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (changes: ClockUpdate) => void;
}

function ClockEditModal({
  period: initialPeriod,
  lengthSeconds: initialLength,
  clockSeconds: initialClock,
  direction: initialDirection,
  regulationPeriods,
  startingNewPeriod,
  isSaving,
  onCancel,
  onSave,
}: ClockEditModalProps) {
  const [period, setPeriod] = useState(initialPeriod);
  const [direction, setDirection] = useState(initialDirection);
  const [lengthMinutes, setLengthMinutes] = useState(
    String(Math.round(initialLength / 60)),
  );
  const [minutes, setMinutes] = useState(String(Math.floor(initialClock / 60)));
  const [secondsField, setSecondsField] = useState(
    String(initialClock % 60).padStart(2, "0"),
  );

  const lengthSeconds = (Number(lengthMinutes) || 0) * 60;
  const clockSeconds =
    (Number(minutes) || 0) * 60 + (Number(secondsField) || 0);

  const lengthError =
    lengthSeconds <= 0
      ? "Enter how long the period is."
      : Number(lengthMinutes) > MAX_PERIOD_MINUTES
        ? `A period can be at most ${MAX_PERIOD_MINUTES} minutes.`
        : null;
  const clockError =
    Number(secondsField) > 59
      ? "Seconds must be 59 or less."
      : !lengthError && clockSeconds > lengthSeconds
        ? "The clock cannot hold more time than the period is long."
        : null;
  const error = lengthError ?? clockError;

  // Setting up a period means putting a full period on the clock, so changing
  // the length carries the clock with it — but only while the clock still
  // reads the old full length, so a correction is never overwritten.
  const handleLengthChange = (raw: string) => {
    const next = digits(raw);
    setLengthMinutes(next);

    const wasFull = clockSeconds === lengthSeconds;
    if (direction === "down" && wasFull) {
      setMinutes(next);
      setSecondsField("00");
    }
  };

  const handleDirectionChange = (next: "down" | "up") => {
    setDirection(next);
    // A count-up period starts at zero and a countdown at its length; anything
    // else would have the clock start at the wrong end of the period.
    setMinutes(next === "up" ? "0" : lengthMinutes);
    setSecondsField("00");
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (error) return;

    onSave({
      current_period: period,
      clock_direction: direction,
      clock_limit_seconds: lengthSeconds,
      clock_seconds: clockSeconds,
    });
  };

  return (
    <Modal show onHide={onCancel} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {startingNewPeriod ? "Start the next period" : "Set the clock"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {startingNewPeriod && (
            <p className="text-muted sb-clock-modal-intro">
              The last period ran out. This starts{" "}
              {periodLabel(period, regulationPeriods).toLowerCase()} with a full
              clock — press play when the puck drops.
            </p>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Period</Form.Label>
            <div className="sb-clock-period-set">
              <Button
                variant="outline-secondary"
                disabled={period <= 1}
                onClick={() => setPeriod((value) => Math.max(value - 1, 1))}
                aria-label="Previous period"
              >
                <i className="bi bi-dash" aria-hidden="true" />
              </Button>
              <span className="sb-clock-period-value">
                {periodLabel(period, regulationPeriods)}
              </span>
              <Button
                variant="outline-secondary"
                onClick={() => setPeriod((value) => value + 1)}
                aria-label="Next period"
              >
                <i className="bi bi-plus" aria-hidden="true" />
              </Button>
            </div>
            <Form.Text className="text-muted">
              Past {periodLabel(regulationPeriods, regulationPeriods)} this is
              overtime.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="clock-direction">
            <Form.Label>Clock runs</Form.Label>
            <div className="sb-clock-direction-set" role="group">
              {(["down", "up"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`sb-clock-direction-btn${
                    option === direction
                      ? " sb-clock-direction-btn--selected"
                      : ""
                  }`}
                  aria-pressed={option === direction}
                  onClick={() => handleDirectionChange(option)}
                >
                  {option === "down" ? "Down" : "Up"}
                </button>
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="clock-period-length">
            <Form.Label>Period length</Form.Label>
            <div className="sb-clock-length">
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                value={lengthMinutes}
                onChange={(event) => handleLengthChange(event.target.value)}
                className="sb-clock-length-input"
              />
              <span className="sb-clock-length-unit">minutes</span>
            </div>
            <Form.Text className="text-muted">
              Set by the league, and editable here for a game that runs to a
              different length.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Time on the clock</Form.Label>
            <div className="sb-clock-fields">
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                aria-label="Minutes"
                value={minutes}
                onChange={(event) => setMinutes(digits(event.target.value))}
                className="sb-clock-input"
              />
              <span className="sb-clock-colon" aria-hidden="true">
                :
              </span>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                aria-label="Seconds"
                value={secondsField}
                onChange={(event) =>
                  setSecondsField(digits(event.target.value))
                }
                className="sb-clock-input"
              />
            </div>
          </Form.Group>

          {error && <div className="sb-clock-error">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSaving || error !== null}
          >
            {isSaving ? "Saving..." : "Set clock"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
